<p-toast></p-toast>

<div>
  <h1 class="text-3xl font-bold text-gray-800 ml-2" i18n>Messaging & Notifications</h1>
  <p class="text-gray-600 mt-2 ml-2" i18n>Manage automated messaging and notification templates</p>
</div>

<div class="bg-white rounded-lg border border-gray-200 mt-5">
  <p class="text-lg font-bold px-3 md:px-8 pt-4" i18n>Message Templates</p>
  <hr class="my-4 border-gray-200 w-full">

  <div class="grid grid-cols-2 gap-4 px-3 md:px-8 pt-4 pb-6">
    <div class="col-span-2 md:col-span-1">
      <p class="font-medium text-gray-700 mb-4" i18n>Available Templates</p>

      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-12">
        <p-progressSpinner 
          [style]="{'width': '50px', 'height': '50px'}" 
          strokeWidth="4" 
          fill="transparent" 
          animationDuration="1s">
        </p-progressSpinner>
        <p class="text-gray-600 mt-4" i18n>Loading templates...</p>
      </div>

      <!-- Template Cards -->
      <div *ngIf="!isLoading" class="md:max-h-[41rem] lg:max-h-[31rem] overflow-y-auto pr-0 md:pr-2" [ngClass]="{'space-y-3': templates.length <= 4, 'space-y-3 pb-2': templates.length > 4}">
        <!-- Dynamic Template Cards -->
        <div 
          *ngFor="let template of templates" 
          class="border rounded-lg p-4 cursor-pointer transition-all duration-200"
          [ngClass]="{
            'border-blue-300 bg-blue-50': isTemplateSelected(template._id),
            'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50': !isTemplateSelected(template._id)
          }"
          (click)="selectTemplate(template._id)">
          <div class="flex justify-between items-start mb-2">
            <div class="flex-1">
              <p class="font-semibold text-base text-gray-800">{{ template.name }}</p>
              <div class="flex items-center gap-2 mt-1">
                <span 
                  class="px-2 py-1 rounded text-xs font-medium"
                  [ngClass]="{
                    'bg-green-100 text-green-700': template.type === 'Email',
                    'bg-blue-100 text-blue-700': template.type === 'App Notification'
                  }">
                  {{ template.type }}
                </span>
                <span 
                  *ngIf="template.subscriberId" 
                  class="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700"
                  title="Customized for your subscription" i18n-title>
                  <span i18n>Custom</span>
                </span>
                <span 
                  *ngIf="!template.subscriberId" 
                  class="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600"
                  title="Default template" i18n-title>
                  <span i18n>Default</span>
                </span>
              </div>
            </div>
          </div>
          <p class="text-gray-600 text-sm mb-3">{{ (template.content && template.content[0] ? template.content[0] : '') | slice:0:55 }}{{ (template.content && template.content[0] && template.content[0].length > 55) ? '...' : '' }}</p>
          <div class="flex items-center text-xs text-gray-500">
            <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clip-rule="evenodd"></path>
            </svg>
            {{ template.trigger }}
          </div>
        </div>
      </div>
    </div>
    
    <div class="col-span-2 md:col-span-1">
      <!-- Loading State for Template Editor -->
      <div *ngIf="isLoading" class="border border-[#E5E7EB] rounded-lg p-6 bg-gray-50">
        <div class="flex flex-col items-center justify-center py-12">
          <p-progressSpinner 
            [style]="{'width': '40px', 'height': '40px'}" 
            strokeWidth="4" 
            fill="transparent" 
            animationDuration="1s">
          </p-progressSpinner>
          <p class="text-gray-600 mt-4" i18n>Loading template editor...</p>
        </div>
      </div>

      <!-- Template Editor -->
      <div class="border border-[#E5E7EB] rounded-lg p-6 bg-gray-50" *ngIf="!isLoading && selectedTemplate">
        <!-- Header with Save Button -->
        <div class="flex justify-between items-center mb-6">
          <div>
            <p class="text-xl font-semibold text-gray-800" i18n>Template Editor</p>
            <p class="text-sm text-gray-600 mt-1" *ngIf="selectedTemplate">
              <span *ngIf="selectedTemplate.subscriberId" class="text-orange-600" i18n>
                Editing custom template for your subscription
              </span>
              <span *ngIf="!selectedTemplate.subscriberId" class="text-blue-600" i18n>
                Editing default template - will create a custom version for your subscription
              </span>
            </p>
          </div>
          <button 
            [disabled]="isSaving"
            class="bg-[#0065AE] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-fit"
            [ngClass]="{
              'hover:bg-gray-700 cursor-pointer': !isSaving,
              'cursor-not-allowed': isSaving
            }"
            (click)="saveChanges()">
            <span *ngIf="!isSaving" i18n>Save Changes</span>
            <span *ngIf="isSaving" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span i18n>Saving...</span>
            </span>
          </button>
        </div>

        <!-- Template Name -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2" i18n>Template Name</label>
          <input 
            type="text" 
            [value]="selectedTemplate.name"
            (input)="updateTemplateName($any($event.target).value)"
            (focus)="onFieldFocus('name', $event)"
            (select)="onSelectionChange('name', $event)"
            (keyup)="onSelectionChange('name', $event)"
            (click)="onSelectionChange('name', $event)"
            data-field="name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900">
        </div>

        <!-- Subject Line -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2" i18n>Subject Line</label>
          <input 
            type="text" 
            [value]="selectedTemplate.subjectLine"
            (input)="updateSubjectLine($any($event.target).value)"
            (focus)="onFieldFocus('subject', $event)"
            (select)="onSelectionChange('subject', $event)"
            (keyup)="onSelectionChange('subject', $event)"
            (click)="onSelectionChange('subject', $event)"
            data-field="subject"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900">
        </div>

        <!-- Dynamic Message Content Fields -->
        <div class="mb-4" *ngFor="let contentIndex of contentFieldsArray; let i = index">
          <label class="block text-sm font-medium text-gray-700 mb-2" i18n>
            Message Content {{ selectedTemplate.noOfContent > 1 ? (i + 1) : '' }}
          </label>
          <textarea
            [value]="getContentValue(i)"
            (input)="updateContent($any($event.target).value, i)"
            (focus)="onFieldFocus('content', $event, i)"
            (select)="onSelectionChange('content', $event, i)"
            (keyup)="onSelectionChange('content', $event, i)"
            (click)="onSelectionChange('content', $event, i)"
            [attr.data-field]="'content_' + i"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32 resize-none text-gray-900">
          </textarea>
        </div>

        <!-- Available Variables -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3" i18n>Available Variables</label>
          <p class="text-xs text-gray-500 mb-2" i18n>Click on any variable to insert it at your cursor position</p>
          <div class="flex flex-wrap gap-2">
            <span 
              *ngFor="let variable of selectedTemplate.availableVariables"
              class="bg-blue-100 text-blue-700 px-3 py-1 font-semibold rounded-full text-xs cursor-pointer hover:bg-blue-200 transition-colors select-none"
              (click)="insertVariable(variable)"
              title="Click to insert at cursor position" i18n-title>
              {{ variable }}
            </span>
          </div>
        </div>

        <!-- Warning Message -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start">
          <svg class="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"></path>
          </svg>
          <p class="text-sm text-yellow-800" i18n>Templates cannot be deleted, only modified to ensure system integrity.</p>
        </div>
      </div>
      
      <!-- Placeholder when no template is selected -->
      <div class="border border-[#E5E7EB] rounded-lg p-6 bg-gray-50 flex items-center justify-center h-96" *ngIf="!selectedTemplate">
        <p class="text-gray-500 text-lg" i18n>Select a template to edit</p>
      </div>
    </div>
  </div>
</div>